import React, { useEffect, useRef, useState, useCallback } from "react";
import { VideoCard } from "../VideoCard";
import "./index.css";

export interface WaterfallItem {
  /** 媒体资源地址 */
  url: string;
  /** 媒体类型，不传则自动根据后缀判断 */
  type?: 'image' | 'video';
  /** 视频海报图（仅视频有效） */
  poster?: string;
  /** 唯一标识 */
  id?: string | number;
  /** 点击回调 */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: (item: any) => void;
  /** 其他自定义数据 */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface WaterfallProps {
  /** 媒体资源数组 */
  items: WaterfallItem[];
  /** 列数（默认会根据屏幕宽度自动响应） */
  columns?: number;
  /** 间隔大小 */
  gap?: number;
  /** 是否启用响应式布局 */
  responsive?: boolean;
  /** 响应式断点配置 */
  breakpoints?: {
    xs?: number;  // < 576px
    sm?: number;  // >= 576px
    md?: number;  // >= 768px
    lg?: number;  // >= 992px
    xl?: number;  // >= 1200px
    xxl?: number; // >= 1600px
  };
  /** 自定义类名 */
  className?: string;
  /** 自定义样式 */
  style?: React.CSSProperties;
  /** 点击回调 */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: (item: any) => void;
}

type DocgenInfo = {
  displayName: string;
  description: string;
  props: Record<string, {
    name: string;
    required: boolean;
    description: string;
    type: { name: string; raw: string };
    defaultValue?: { value: string };
  }>;
};

type ComponentWithDocgen = React.FC<WaterfallProps> & { __docgenInfo?: DocgenInfo };

// 判断是否为视频 URL
const isVideoUrl = (url: string): boolean => {
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.wmv', '.flv', '.mkv'];
  return videoExtensions.some(ext => url.toLowerCase().includes(ext));
};

// 获取媒体类型
const getMediaType = (item: WaterfallItem): 'image' | 'video' => {
  if (item.type) return item.type;
  if (isVideoUrl(item.url)) return 'video';
  return 'image';
};

/** 瀑布流布局组件，支持图片和视频混合展示 */
export const Waterfall: React.FC<WaterfallProps> = ({
  items,
  columns,
  gap = 0,
  responsive = true,
  breakpoints = {
    xs: 1,
    sm: 2,
    md: 3,
    lg: 4,
    xl: 5,
    xxl: 6
  },
  className,
  style,
  onClick
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [columnCount, setColumnCount] = useState<number>(columns || breakpoints.md || 3);
  const itemRefs = useRef<Map<string | number, HTMLDivElement>>(new Map());

  // 计算当前应该显示的列数
  const calculateColumns = useCallback(() => {
    if (!responsive || columns) {
      return columns || breakpoints.md || 3;
    }

    const width = window.innerWidth;
    
    if (width < 576) return breakpoints.xs || 1;
    if (width < 768) return breakpoints.sm || 2;
    if (width < 992) return breakpoints.md || 3;
    if (width < 1200) return breakpoints.lg || 4;
    if (width < 1600) return breakpoints.xl || 5;
    return breakpoints.xxl || 6;
  }, [responsive, columns, breakpoints]);

  // 布局计算
  const calculateLayout = useCallback(() => {
    if (!containerRef.current) return;

    const cols = columnCount;
    const container = containerRef.current;
    const containerWidth = container.offsetWidth;
    const columnWidth = (containerWidth - gap * (cols - 1)) / cols;
    
    // 初始化每列的高度
    const heights = new Array(cols).fill(0);

    // 为每个元素计算位置
    items.forEach((item, index) => {
      const key = item.id || index;
      const element = itemRefs.current.get(key);
      
      if (!element) return;

      // 找到最短的列
      const minHeight = Math.min(...heights);
      const columnIndex = heights.indexOf(minHeight);

      // 计算位置
      const left = columnIndex * (columnWidth + gap);
      const top = heights[columnIndex];

      // 设置元素位置和宽度
      element.style.position = 'absolute';
      element.style.left = `${left}px`;
      element.style.top = `${top}px`;
      element.style.width = `${columnWidth}px`;

      // 更新列高度
      heights[columnIndex] += element.offsetHeight + gap;
    });

    // 设置容器高度
    const maxHeight = Math.max(...heights);
    container.style.height = `${maxHeight}px`;
  }, [items, columnCount, gap]);

  // 处理窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      const newColumns = calculateColumns();
      if (newColumns !== columnCount) {
        setColumnCount(newColumns);
      }
    };

    if (responsive) {
      window.addEventListener('resize', handleResize);
      handleResize();
      
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [responsive, calculateColumns, columnCount]);

  // 重新计算布局
  useEffect(() => {
    // 等待所有图片和视频加载完成后再计算布局
    const timer = setTimeout(() => {
      calculateLayout();
    }, 100);

    return () => clearTimeout(timer);
  }, [items, columnCount, calculateLayout]);

  // 图片加载完成后重新计算布局
  const handleImageLoad = useCallback(() => {
    calculateLayout();
  }, [calculateLayout]);

  // 处理项目点击
  const handleItemClick = useCallback((item: WaterfallItem) => {
    // 优先调用组件级别的 onClick
    if (onClick) {
      onClick(item);
    }
    // 然后调用 item 级别的 onClick
    item.onClick?.(item);
  }, [onClick]);

  return (
    <div
      ref={containerRef}
      className={`waterfall-container ${className || ''}`}
      style={{
        position: 'relative',
        width: '100%',
        ...style
      }}
    >
      {items.map((item, index) => {
        const key = item.id || index;
        const mediaType = getMediaType(item);

        return (
          <div
            key={key}
            ref={el => {
              if (el) {
                itemRefs.current.set(key, el);
              } else {
                itemRefs.current.delete(key);
              }
            }}
            className="waterfall-item"
          >
            {mediaType === 'video' ? (
              <div style={{ 
                width: '100%', 
                position: 'relative', 
                paddingBottom: '56.25%',
                cursor: (onClick || item.onClick) ? 'pointer' : 'default'
              }}>
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                  <VideoCard
                    src={item.url}
                    poster={item.poster}
                    width="100%"
                    height="100%"
                    onClick={() => handleItemClick(item)}
                    onVideoLoaded={handleImageLoad}
                    autoPlayOnHover={true}
                    showPlayButton={false}
                  />
                </div>
              </div>
            ) : (
              <div 
                className="waterfall-image-wrapper"
                onClick={() => handleItemClick(item)}
                style={{ cursor: (onClick || item.onClick) ? 'pointer' : 'default' }}
              >
                <img
                  src={item.url}
                  alt={`瀑布流图片 ${index + 1}`}
                  className="waterfall-image"
                  onLoad={handleImageLoad}
                  onError={handleImageLoad}
                  style={{ display: 'block', width: '100%', height: 'auto' }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// 手动添加 docgen 信息
(Waterfall as ComponentWithDocgen).__docgenInfo = {
  displayName: "Waterfall",
  description: "瀑布流布局组件，支持图片和视频混合展示",
  props: {
    items: {
      name: "items",
      required: true,
      description: "媒体资源数组",
      type: { name: "WaterfallItem[]", raw: "WaterfallItem[]" }
    },
    columns: {
      name: "columns",
      required: false,
      description: "列数（默认会根据屏幕宽度自动响应）",
      type: { name: "number", raw: "number" }
    },
    gap: {
      name: "gap",
      required: false,
      description: "间隔大小",
      type: { name: "number", raw: "number" },
      defaultValue: { value: "0" }
    },
    responsive: {
      name: "responsive",
      required: false,
      description: "是否启用响应式布局",
      type: { name: "boolean", raw: "boolean" },
      defaultValue: { value: "true" }
    },
    breakpoints: {
      name: "breakpoints",
      required: false,
      description: "响应式断点配置",
      type: { name: "object", raw: "{ xs?: number; sm?: number; md?: number; lg?: number; xl?: number; xxl?: number; }" }
    },
    className: {
      name: "className",
      required: false,
      description: "自定义类名",
      type: { name: "string", raw: "string" }
    },
    style: {
      name: "style",
      required: false,
      description: "自定义样式",
      type: { name: "CSSProperties", raw: "React.CSSProperties" }
    },
    onClick: {
      name: "onClick",
      required: false,
      description: "点击回调",
      type: { name: "function", raw: "(item: any) => void" }
    }
  }
};

